/**
 * MealPlannerScreen.jsx
 * KI-Mahlzeitenplaner für Mobile App
 * 
 * Features:
 * 1. KI-Planung: 3 Varianten pro Mahlzeit
 * 2. Eigene Idee: Zutaten eingeben, KI berechnet Mengen
 * 3. Smart-Tipps: Vorschläge basierend auf Tagesfortschritt
 */
import React, { useState, useEffect } from 'react';
import { X, Sparkles, ChefHat, Lightbulb, RefreshCw, Check, Plus, ArrowLeft, ChevronRight, ChevronUp, ChevronDown, GripVertical, Pin, PinOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { sortFavoritesByPinnedOrder } from '@/utils/favoritesOrder';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const MealPlannerScreen = ({
  isOpen,
  onClose,
  portalToken,
  customerId,
  onMealAdded,
  dailyProgress = { calories: 0, protein: 0, carbs: 0, fat: 0 },
  dailyGoals = { calories: 2000, protein: 120, carbs: 200, fat: 65 }
}) => {
  // View state: 'main', 'ai-plan', 'own-idea', 'variants'
  const [view, setView] = useState('main');
  
  // AI Plan state
  const [mealPlan, setMealPlan] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [currentMealType, setCurrentMealType] = useState(null);
  
  // Own idea state
  const [selectedMealForOwnIdea, setSelectedMealForOwnIdea] = useState('dinner');
  const [ownIngredients, setOwnIngredients] = useState([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [calculatingOwn, setCalculatingOwn] = useState(false);
  const [ownResult, setOwnResult] = useState(null);
  
  // Smart tips
  const [smartTips, setSmartTips] = useState(null);
  const [loadingTips, setLoadingTips] = useState(false);
  
  // Favorites
  const [favorites, setFavorites] = useState([]);
  
  // Normalize goals - handle both naming conventions (daily_calories vs calories)
  const normalizedGoals = {
    calories: dailyGoals.daily_calories || dailyGoals.calories || 2000,
    protein: dailyGoals.daily_protein || dailyGoals.protein || 120,
    carbs: dailyGoals.daily_carbs || dailyGoals.carbs || 200,
    fat: dailyGoals.daily_fat || dailyGoals.fat || 65
  };
  
  // Calculate progress
  const progressPercent = normalizedGoals.calories > 0 
    ? Math.round((dailyProgress.calories / normalizedGoals.calories) * 100) 
    : 0;
  
  const remainingCalories = normalizedGoals.calories - dailyProgress.calories;
  const remainingProtein = normalizedGoals.protein - dailyProgress.protein;
  const remainingCarbs = normalizedGoals.carbs - dailyProgress.carbs;
  const remainingFat = normalizedGoals.fat - dailyProgress.fat;
  
  useEffect(() => {
    if (isOpen && portalToken && customerId) {
      loadFavorites();
      if (progressPercent >= 30) {
        loadSmartTips();
      }
    }
  }, [isOpen, portalToken, customerId]);
  
  const loadFavorites = async () => {
    try {
      const response = await axios.get(
        `${API}/meal-assistant/favorite-foods/${customerId}`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      const foods = response.data?.favorites || response.data?.foods || [];
      setFavorites(sortFavoritesByPinnedOrder(foods));
    } catch (err) {
      console.error('Load favorites error:', err);
    }
  };

  const persistFavoritesOrder = async (orderedFavorites) => {
    try {
      await axios.put(
        `${API}/meal-assistant/favorite-foods/${customerId}/reorder`,
        { ordered_ids: orderedFavorites.map((fav) => fav.id) },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
    } catch (err) {
      console.error('Persist favorites order error:', err);
      toast.error('Favoriten-Reihenfolge konnte nicht gespeichert werden');
      loadFavorites();
    }
  };

  const moveFavorite = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= favorites.length) return;
    const updated = [...favorites];
    const [item] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, item);
    setFavorites(updated);
    persistFavoritesOrder(updated);
  };

  const reorderFavoriteByDrag = (dragIndex, dropIndex) => {
    if (dragIndex === dropIndex || dragIndex < 0 || dropIndex < 0) return;
    const updated = [...favorites];
    const [item] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, item);
    setFavorites(updated);
    persistFavoritesOrder(updated);
  };

  const togglePinFavorite = async (fav) => {
    try {
      await axios.put(
        `${API}/meal-assistant/favorite-foods/${customerId}/${fav.id}/pin`,
        { pinned: !fav.is_pinned },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      await loadFavorites();
      toast.success(!fav.is_pinned ? 'Favorit fixiert' : 'Fixierung entfernt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fixierung konnte nicht geändert werden');
    }
  };
  
  const loadSmartTips = async () => {
    setLoadingTips(true);
    try {
      const response = await axios.post(
        `${API}/meal-assistant/smart-tips`,
        {
          customer_id: customerId,
          remaining_calories: remainingCalories,
          remaining_protein: remainingProtein,
          remaining_carbs: remainingCarbs,
          remaining_fat: remainingFat,
          progress_percent: progressPercent
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setSmartTips(response.data);
    } catch (err) {
      console.error('Load smart tips error:', err);
    } finally {
      setLoadingTips(false);
    }
  };
  
  const generateMealPlan = async () => {
    setGeneratingPlan(true);
    try {
      const response = await axios.post(
        `${API}/meal-assistant/generate-day-plan`,
        { customer_id: customerId },
        { headers: { Authorization: `Bearer ${portalToken}` }, timeout: 60000 }
      );
      setMealPlan(response.data);
      setView('ai-plan');
    } catch (err) {
      toast.error('Fehler beim Erstellen des Plans');
    } finally {
      setGeneratingPlan(false);
    }
  };
  
  const applySmartTip = (tip) => {
    if (onMealAdded && tip.portions) {
      tip.portions.forEach(async (portion) => {
        await onMealAdded({
          meal_type: tip.meal_type || 'snack',
          food_name: `${portion.ingredient} (${portion.grams}g)`,
          serving_size: portion.grams,
          calories: portion.calories,
          protein: portion.protein,
          carbs: portion.carbs,
          fat: portion.fat
        });
      });
      toast.success(`${tip.name} hinzugefügt!`);
    }
  };
  
  const addIngredient = () => {
    if (newIngredient.trim() && !ownIngredients.includes(newIngredient.trim())) {
      setOwnIngredients([...ownIngredients, newIngredient.trim()]);
      setNewIngredient('');
    }
  };
  
  const removeIngredient = (index) => {
    setOwnIngredients(ownIngredients.filter((_, i) => i !== index));
  };
  
  const calculateOwnMeal = async () => {
    if (ownIngredients.length === 0) {
      toast.error('Bitte Zutaten hinzufügen');
      return;
    }
    
    setCalculatingOwn(true);
    try {
      const response = await axios.post(
        `${API}/meal-assistant/calculate-portions`,
        {
          customer_id: customerId,
          ingredients: ownIngredients,
          meal_type: selectedMealForOwnIdea,
          remaining_calories: remainingCalories > 0 ? Math.round(remainingCalories) : null,
          remaining_protein: remainingProtein >= 0 && remainingCalories > 0 ? Math.round(remainingProtein) : null,
          remaining_carbs: remainingCarbs >= 0 && remainingCalories > 0 ? Math.round(remainingCarbs) : null,
          remaining_fat: remainingFat >= 0 && remainingCalories > 0 ? Math.round(remainingFat) : null
        },
        { headers: { Authorization: `Bearer ${portalToken}` }, timeout: 30000 }
      );
      setOwnResult(response.data);
    } catch (err) {
      toast.error('Berechnung fehlgeschlagen');
    } finally {
      setCalculatingOwn(false);
    }
  };
  
  const applyOwnResult = () => {
    if (ownResult && onMealAdded) {
      ownResult.portions.forEach(async (portion) => {
        await onMealAdded({
          meal_type: selectedMealForOwnIdea,
          food_name: `${portion.ingredient} (${portion.grams}g)`,
          serving_size: portion.grams,
          calories: portion.calories,
          protein: portion.protein,
          carbs: portion.carbs,
          fat: portion.fat
        });
      });
      toast.success('Mahlzeit hinzugefügt!');
      resetAndClose();
    }
  };
  
  const applyMealPlan = () => {
    if (!mealPlan || !mealPlan.meal_plan || !onMealAdded) return;
    
    Object.entries(mealPlan.meal_plan).forEach(([mealType, mealData]) => {
      const variantIdx = selectedVariants[mealType] ?? 0;
      const variant = mealData.variants?.[variantIdx];
      
      if (variant && variant.items) {
        variant.items.forEach(async (item) => {
          await onMealAdded({
            meal_type: mealType,
            food_name: item.name,
            serving_size: item.amount,
            calories: item.calories || 0,
            protein: item.protein || 0,
            carbs: item.carbs || 0,
            fat: item.fat || 0
          });
        });
      }
    });
    
    toast.success('Tagesplan übernommen!');
    resetAndClose();
  };
  
  const resetAndClose = () => {
    setView('main');
    setMealPlan(null);
    setSelectedVariants({});
    setOwnIngredients([]);
    setOwnResult(null);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="absolute inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700 flex items-center gap-3">
        <button onClick={view === 'main' ? resetAndClose : () => setView('main')} className="p-2 bg-zinc-800 rounded-lg">
          {view === 'main' ? <X className="w-5 h-5 text-zinc-400" /> : <ArrowLeft className="w-5 h-5 text-zinc-400" />}
        </button>
        <div>
          <h2 className="text-white font-bold">
            {view === 'main' && 'Tagesplaner'}
            {view === 'ai-plan' && 'KI-Tagesplan'}
            {view === 'own-idea' && 'Eigene Idee'}
          </h2>
          <p className="text-zinc-500 text-xs">
            {view === 'main' && 'Plane deinen Tag'}
            {view === 'ai-plan' && 'Wähle Varianten'}
            {view === 'own-idea' && 'Gib deine Zutaten ein'}
          </p>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Main Menu */}
        {view === 'main' && (
          <>
            {/* Progress & Smart Tips */}
            {progressPercent >= 30 && (
              <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-4 border border-yellow-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 font-medium">{progressPercent}% erreicht</span>
                  </div>
                  <span className="text-zinc-400 text-sm">{Math.round(remainingCalories)} kcal übrig</span>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-zinc-700 rounded-full h-2 mb-3">
                  <div 
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2 rounded-full"
                    style={{ width: `${Math.min(progressPercent, 100)}%` }}
                  />
                </div>
                
                {/* Tips */}
                {smartTips?.suggestions?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-zinc-400 uppercase">KI-Vorschläge:</p>
                    {smartTips.suggestions.map((tip, i) => (
                      <button
                        key={i}
                        onClick={() => applySmartTip(tip)}
                        className="w-full flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{tip.emoji || '🍴'}</span>
                          <div className="text-left">
                            <p className="text-white text-sm font-medium">{tip.name}</p>
                            <p className="text-zinc-500 text-xs">{tip.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-orange-400 text-sm">{Math.round(tip.calories || 0)} kcal</span>
                          <Plus className="w-4 h-4 text-green-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {loadingTips && (
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Lade Tipps...
                  </div>
                )}
              </div>
            )}
            
            {/* Main Options */}
            <div className="space-y-3">
              {/* KI-Planung */}
              <button
                onClick={generateMealPlan}
                disabled={generatingPlan}
                className="w-full p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl flex items-center gap-4"
              >
                <div className="w-14 h-14 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  {generatingPlan ? (
                    <RefreshCw className="w-7 h-7 text-yellow-400 animate-spin" />
                  ) : (
                    <Sparkles className="w-7 h-7 text-yellow-400" />
                  )}
                </div>
                <div className="text-left flex-1">
                  <p className="text-yellow-400 font-bold">KI plant deinen Tag</p>
                  <p className="text-zinc-500 text-sm">3 Varianten pro Mahlzeit zur Auswahl</p>
                </div>
                <ChevronRight className="w-5 h-5 text-yellow-400" />
              </button>
              
              {/* Eigene Idee */}
              <button
                onClick={() => setView('own-idea')}
                className="w-full p-4 bg-zinc-800/50 border border-zinc-700 rounded-xl flex items-center gap-4"
              >
                <div className="w-14 h-14 bg-zinc-700 rounded-xl flex items-center justify-center">
                  <ChefHat className="w-7 h-7 text-zinc-300" />
                </div>
                <div className="text-left flex-1">
                  <p className="text-white font-bold">Eigene Idee</p>
                  <p className="text-zinc-500 text-sm">Gib Zutaten ein, KI berechnet Mengen</p>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
          </>
        )}
        
        {/* AI Plan View */}
        {view === 'ai-plan' && mealPlan && (
          <div className="space-y-4">
            {/* Goals */}
            <div className="grid grid-cols-4 gap-2 p-3 bg-zinc-800 rounded-lg">
              <div className="text-center">
                <p className="text-lg font-bold text-orange-400">{mealPlan.goals?.daily_calories || '-'}</p>
                <p className="text-xs text-zinc-400">kcal</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-blue-400">{mealPlan.goals?.daily_protein || '-'}g</p>
                <p className="text-xs text-zinc-400">Protein</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-yellow-400">{mealPlan.goals?.daily_carbs || '-'}g</p>
                <p className="text-xs text-zinc-400">Carbs</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-red-400">{mealPlan.goals?.daily_fat || '-'}g</p>
                <p className="text-xs text-zinc-400">Fett</p>
              </div>
            </div>
            
            {/* Meals */}
            {Object.entries(mealPlan.meal_plan || {}).map(([mealType, mealData]) => (
              <div key={mealType} className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    {mealType === 'breakfast' ? '🌅' : mealType === 'lunch' ? '☀️' : mealType === 'dinner' ? '🌙' : '🥨'}
                    {mealData.name}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedMealForOwnIdea(mealType);
                      setView('own-idea');
                    }}
                    className="text-yellow-400 text-xs"
                  >
                    ✏️ Eigene Idee
                  </button>
                </div>
                
                {/* Variants */}
                <div className="space-y-2">
                  {mealData.variants?.map((variant, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedVariants(prev => ({ ...prev, [mealType]: idx }))}
                      className={`w-full p-3 rounded-lg border-2 text-left ${
                        selectedVariants[mealType] === idx
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-zinc-700 bg-zinc-800'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white text-sm font-medium">{variant.name}</p>
                        {selectedVariants[mealType] === idx && <Check className="w-4 h-4 text-green-400" />}
                      </div>
                      <p className="text-zinc-400 text-xs">
                        {variant.items?.slice(0, 2).map(i => `${i.amount}${i.unit} ${i.name}`).join(', ')}
                        {variant.items?.length > 2 && ` +${variant.items.length - 2}`}
                      </p>
                      <p className="text-orange-400 text-xs mt-1">{Math.round(variant.total_calories || 0)} kcal</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Apply Button */}
            <button
              onClick={applyMealPlan}
              className="w-full py-4 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Plan übernehmen
            </button>
          </div>
        )}
        
        {/* Own Idea View */}
        {view === 'own-idea' && (
          <div className="space-y-4">
            {/* Meal Type */}
            <div>
              <p className="text-zinc-400 text-xs uppercase mb-2">Für welche Mahlzeit?</p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { key: 'breakfast', label: 'Frühstück', emoji: '🌅' },
                  { key: 'lunch', label: 'Mittag', emoji: '☀️' },
                  { key: 'dinner', label: 'Abend', emoji: '🌙' },
                  { key: 'snack', label: 'Snack', emoji: '🥨' }
                ].map(({ key, label, emoji }) => (
                  <button
                    key={key}
                    onClick={() => setSelectedMealForOwnIdea(key)}
                    className={`p-3 rounded-lg text-center ${
                      selectedMealForOwnIdea === key
                        ? 'bg-yellow-500/20 border-2 border-yellow-500'
                        : 'bg-zinc-800 border-2 border-transparent'
                    }`}
                  >
                    <span className="text-xl">{emoji}</span>
                    <p className={`text-xs mt-1 ${selectedMealForOwnIdea === key ? 'text-yellow-400' : 'text-zinc-400'}`}>
                      {label}
                    </p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Remaining Info */}
            <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
              <p className="text-blue-400 text-xs font-medium mb-2">Verbleibend:</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-white font-bold">{Math.round(remainingCalories)}</p>
                  <p className="text-zinc-500 text-xs">kcal</p>
                </div>
                <div>
                  <p className="text-blue-400 font-bold">{Math.round(remainingProtein)}g</p>
                  <p className="text-zinc-500 text-xs">P</p>
                </div>
                <div>
                  <p className="text-green-400 font-bold">{Math.round(remainingCarbs)}g</p>
                  <p className="text-zinc-500 text-xs">K</p>
                </div>
                <div>
                  <p className="text-orange-400 font-bold">{Math.round(remainingFat)}g</p>
                  <p className="text-zinc-500 text-xs">F</p>
                </div>
              </div>
            </div>
            
            {/* Ingredient Input */}
            <div>
              <p className="text-zinc-400 text-xs uppercase mb-2">Zutaten eingeben</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newIngredient}
                  onChange={(e) => setNewIngredient(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addIngredient()}
                  placeholder="z.B. Reis, Poulet..."
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                />
                <button onClick={addIngredient} className="px-4 py-2 bg-yellow-500 rounded-lg">
                  <Plus className="w-5 h-5 text-black" />
                </button>
              </div>
            </div>
            
            {/* Favorites */}
            {favorites.length > 0 && (
              <div>
                <p className="text-zinc-400 text-xs uppercase mb-2">Favoriten</p>
                <div className="space-y-2" data-testid="mobile-favorite-food-order-list">
                  {favorites.slice(0, 12).map((fav, i) => {
                    const name = fav.name || fav.food_name;
                    return (
                      <div
                        key={fav.id || `${name}-${i}`}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', String(i))}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          const dragIndex = Number(e.dataTransfer.getData('text/plain'));
                          reorderFavoriteByDrag(dragIndex, i);
                        }}
                        className="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5"
                        data-testid={`mobile-favorite-food-order-item-${fav.id || i}`}
                      >
                        <GripVertical className="w-4 h-4 text-zinc-500" />
                        <button
                          onClick={() => !ownIngredients.includes(name) && setOwnIngredients([...ownIngredients, name])}
                          className={`flex-1 text-left text-xs ${ownIngredients.includes(name) ? 'text-zinc-500' : 'text-zinc-200'}`}
                        >
                          {name}
                          {fav.is_pinned && <span className="ml-1 text-[10px] text-yellow-400">📌</span>}
                        </button>
                        <button
                          onClick={() => togglePinFavorite(fav)}
                          className="h-7 w-7 rounded bg-zinc-800 text-zinc-300"
                          data-testid={`mobile-favorite-food-pin-toggle-${fav.id || i}`}
                        >
                          {fav.is_pinned ? <PinOff className="w-3 h-3 mx-auto" /> : <Pin className="w-3 h-3 mx-auto" />}
                        </button>
                        <button
                          onClick={() => moveFavorite(i, -1)}
                          disabled={i === 0}
                          className="h-7 w-7 rounded bg-zinc-800 text-zinc-300 disabled:opacity-30"
                          data-testid={`mobile-favorite-food-move-up-${fav.id || i}`}
                        >
                          <ChevronUp className="w-3 h-3 mx-auto" />
                        </button>
                        <button
                          onClick={() => moveFavorite(i, 1)}
                          disabled={i === favorites.length - 1}
                          className="h-7 w-7 rounded bg-zinc-800 text-zinc-300 disabled:opacity-30"
                          data-testid={`mobile-favorite-food-move-down-${fav.id || i}`}
                        >
                          <ChevronDown className="w-3 h-3 mx-auto" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Selected */}
            {ownIngredients.length > 0 && (
              <div>
                <p className="text-zinc-400 text-xs uppercase mb-2">Ausgewählt ({ownIngredients.length})</p>
                <div className="flex flex-wrap gap-2">
                  {ownIngredients.map((ing, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 rounded-full text-yellow-400 text-sm">
                      {ing}
                      <button onClick={() => removeIngredient(i)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Calculate */}
            <button
              onClick={calculateOwnMeal}
              disabled={calculatingOwn || ownIngredients.length === 0}
              className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black rounded-xl font-bold disabled:opacity-50"
            >
              {calculatingOwn ? (
                <span className="flex items-center justify-center gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Berechne...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Portionen berechnen
                </span>
              )}
            </button>
            
            {/* Result */}
            {ownResult && ownResult.portions && (
              <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-5 h-5 text-green-400" />
                  <h3 className="text-white font-bold">Empfohlene Portionen</h3>
                </div>
                
                {ownResult.portions.map((portion, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg mb-2">
                    <div>
                      <p className="text-white font-medium">{portion.ingredient}</p>
                      <p className="text-zinc-500 text-xs">{Math.round(portion.calories || 0)} kcal</p>
                    </div>
                    <p className="text-yellow-400 font-bold text-lg">{portion.grams}g</p>
                  </div>
                ))}
                
                <div className="mt-3 pt-3 border-t border-green-500/30 flex justify-between">
                  <span className="text-zinc-400">Gesamt:</span>
                  <span className="text-white font-bold">{Math.round(ownResult.totals?.calories || 0)} kcal</span>
                </div>
                
                <button onClick={applyOwnResult} className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-bold">
                  <Check className="w-4 h-4 inline mr-2" />
                  Übernehmen
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MealPlannerScreen;
